import { IconDefinition } from "@fortawesome/fontawesome-svg-core"
import {
  faCalendarDays,
  faChartUser,
  faEye,
  faHandPointUp,
  faHandshake,
  faMoneyBill,
  faSackDollar,
  faTrophy
} from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Typography } from "@mui/material"
import { translate } from "app/language/service"
import { IRootState } from "app/model"
import { getFormatedNumber } from "app/tools/number"
import { CURRENCIES } from "currency/model"
import { getValueInUserCurrency } from "currency/tools/common"
import { getCurrencySymbol, getEcpm } from "form/inventory/tools/common"
import React from "react"
import { connect } from "react-redux"
import { getDefaultRevenueTimeFilter, getRevenueTimeFilters, IReportTotal } from "reports/model"
import {
  getRevenueChartLabelBids,
  getRevenueChartLabelBuys,
  getRevenueChartLabelGrow,
  getRevenueChartLabelImpression,
  getRevenueChartLabelWin,
  refreshGroupedByRevenueReports
} from "reports/tools/revenue"
import { Chart } from "ui/components/Chart"
import { FlexBox } from "ui/components/FlexBox"
import { LinearLoader } from "ui/components/LinearLoader"
import { PageHeader } from "ui/components/main-layout/PageHeader"
import { TimeFilter } from "ui/components/TimeFilter"
import { IChartDataSet } from "ui/configuration/model"
import { isPhoneScreen } from "ui/tools/responsive"
import { getUserCurrencyId } from "user/tools/common"

interface IRevenuePageProps {
  data: IChartDataSet[]
  total: IReportTotal | null
  isLoading: boolean
  hasLoadingError: boolean
  isPhone: boolean
  contentWidth: number
}

interface ITitleInfoProps {
  id: string
  title: string
  value: number | string
  icon: IconDefinition
  fullWidth?: boolean
  inverseColors?: boolean
}

const TitleInfo = React.memo(({ id, icon, title, inverseColors, value, fullWidth }: ITitleInfoProps) => (
  <FlexBox
    id={id}
    gap
    jc="space-between"
    sx={{
      "p": "1rem",
      "border": theme => theme.palette.border,
      "borderRadius": ".3rem",
      "backgroundColor": theme => (inverseColors ? theme.palette.primary.main : theme.palette.background.paper),
      "& .icon": { color: theme => (inverseColors ? theme.palette.text.primary : theme.palette.primary.main) },
      "width": fullWidth ? "100%" : undefined,
      "minWidth": fullWidth ? undefined : "19rem"
    }}>
    <FontAwesomeIcon icon={icon} size="3x" className="icon" />
    <FlexBox vertical ai="end">
      <Typography id={`${id}-title`} color="textPrimary" fontSize="1rem">
        {title}
      </Typography>
      <Typography id={`${id}-value`} color="textPrimary" fontSize="2rem" fontWeight="500">
        {value}
      </Typography>
    </FlexBox>
  </FlexBox>
))

const RevenuePageFn = React.memo(
  ({ data, total, contentWidth, isPhone, isLoading, hasLoadingError }: IRevenuePageProps) => {
    const [timeFilter, setTimeFilter] = React.useState(getDefaultRevenueTimeFilter())
    React.useEffect(() => {
      refreshGroupedByRevenueReports(timeFilter)
    }, [timeFilter])
    const noTotalToDisplay = !total || (total.wins === 0 && total.buys === 0 && total.impressions === 0)
    const growLabel = getRevenueChartLabelGrow()
    const bidsLabel = getRevenueChartLabelBids()
    const winsLabel = getRevenueChartLabelWin()
    const impressionsLabel = getRevenueChartLabelImpression()
    const buysLabel = getRevenueChartLabelBuys()
    const oneColumnLayout = contentWidth < 1010
    return (
      <FlexBox vertical fullWidth fullHeight ai="start" jc="start" sx={{ animation: "fadeIn 600ms" }}>
        <PageHeader title={`${translate("revenue.title")} - ${translate("revenue.subtitle")}`} />
        <FlexBox
          fullWidth
          fullHeight
          jc="start"
          ai="start"
          vertical
          gap
          sx={{
            "p": "1rem",
            "pt": "2rem",
            "overflow": "auto",
            "& .chart": {
              backgroundColor: theme => theme.palette.background.paper,
              border: theme => theme.palette.border,
              borderRadius: ".3rem"
            }
          }}>
          <FlexBox
            gap
            wrap
            fullWidth
            ai="start"
            vertical={oneColumnLayout}
            sx={{
              "& .info-row": {
                width: isPhone ? undefined : "10rem",
                gap: ".4rem"
              },
              "& .info-row-title": {
                fontSize: isPhone ? ".75rem" : undefined
              },
              "& .info-row-value": {
                color: theme => theme.palette.text.primary,
                fontSize: isPhone ? ".75rem" : "1.2rem",
                fontWeight: 500
              }
            }}>
            <FlexBox
              gap
              sx={{
                "p": "1rem",
                "border": theme => theme.palette.border,
                "borderRadius": ".3rem",
                "width": isPhone ? "100%" : undefined,
                "backgroundColor": theme => theme.palette.background.paper,
                "minHeight": "6.6rem",
                "minWidth": isPhone ? undefined : "19rem",
                "& .icon": { color: theme => theme.palette.primary.main }
              }}>
              <FontAwesomeIcon icon={faCalendarDays} size="3x" className="icon" />
              <FlexBox vertical ai="start" sx={{ width: "calc(100% - 4rem)" }}>
                <Typography color="textPrimary">{translate("label.period")}</Typography>
                <TimeFilter
                  id="revenue-period"
                  filters={getRevenueTimeFilters()}
                  value={timeFilter}
                  disabled={isLoading}
                  label={""}
                  onChange={newTimeFilter => setTimeFilter(newTimeFilter)}
                  sx={{
                    mt: ".4rem",
                    minWidth: "100%"
                  }}
                />
              </FlexBox>
            </FlexBox>
            {!noTotalToDisplay && (
              <FlexBox wrap gap jc="space-evenly" sx={{ width: oneColumnLayout ? "100%" : "calc(100% - 21rem)" }}>
                <TitleInfo
                  id="total-revenue"
                  title={translate("label.total.revenues")}
                  value={`${getCurrencySymbol(getUserCurrencyId())} ${getFormatedNumber(
                    getValueInUserCurrency(CURRENCIES.CAD, total.spend)
                  )}`}
                  icon={faSackDollar}
                  fullWidth={isPhone}
                />
                <TitleInfo
                  id="total-ecpm"
                  title={translate("label.ecpm.average")}
                  value={`${getCurrencySymbol(getUserCurrencyId())} ${getFormatedNumber(
                    getValueInUserCurrency(CURRENCIES.CAD, getEcpm(total.spend, total.impressions))
                  )}`}
                  icon={faChartUser}
                  fullWidth={isPhone}
                />
                <TitleInfo
                  id="total-impressions"
                  title={translate("label.total.impressions")}
                  value={getFormatedNumber(Math.round(total.impressions))}
                  icon={faEye}
                  fullWidth={isPhone}
                />
                <TitleInfo
                  id="total-bids"
                  title={translate("label.total.bids")}
                  value={getFormatedNumber(Math.round(total.bids))}
                  icon={faHandPointUp}
                  fullWidth={isPhone}
                />
                <TitleInfo
                  id="total-wins"
                  title={translate("label.total.wins")}
                  value={getFormatedNumber(Math.round(total.wins))}
                  icon={faTrophy}
                  fullWidth={isPhone}
                />
                <TitleInfo
                  id="total-buys"
                  title={translate("label.total.buys")}
                  value={getFormatedNumber(Math.round(total.buys))}
                  icon={faHandshake}
                  fullWidth={isPhone}
                />
              </FlexBox>
            )}
          </FlexBox>
          {hasLoadingError ? (
            <Typography color="error">{translate("revenue.loading.error")}</Typography>
          ) : isLoading ? (
            <LinearLoader />
          ) : !total || (total.wins === 0 && total.buys === 0 && total.impressions === 0) ? (
            <FlexBox
              fullWidth
              gap
              wrap
              sx={{ "mt": "2rem", "& .icon": { color: theme => theme.palette.primary.main } }}>
              <FontAwesomeIcon icon={faMoneyBill} size="3x" className="icon" />
              <Typography color="textPrimary">{translate("revenue.no.data")}</Typography>
            </FlexBox>
          ) : (
            <>
              <FlexBox fullWidth vertical={oneColumnLayout} wrap gap>
                <FlexBox
                  sx={{
                    width: oneColumnLayout ? "100%" : "calc(50% - .5rem)",
                    minHeight: isPhone ? undefined : "30rem"
                  }}
                  className="chart">
                  <Chart type="bar" divId="grow-chart" dataSets={data.filter(d => d.label === growLabel)} />
                </FlexBox>
                <FlexBox
                  sx={{
                    width: oneColumnLayout ? "100%" : "calc(50% - .5rem)",
                    minHeight: isPhone ? undefined : "30rem"
                  }}
                  className="chart">
                  <Chart
                    type="bar"
                    divId="bid-chart"
                    dataSets={data.filter(d => d.label === bidsLabel || d.label === winsLabel)}
                  />
                </FlexBox>
              </FlexBox>
              <FlexBox fullWidth sx={{ mb: "4rem", minHeight: isPhone ? undefined : "40rem" }} className="chart">
                <Chart
                  type="bar"
                  divId="impression-chart"
                  dataSets={data.filter(d => d.label === impressionsLabel || d.label === buysLabel)}
                />
              </FlexBox>
            </>
          )}
        </FlexBox>
      </FlexBox>
    )
  }
)

export const RevenuePage = connect(
  (state: IRootState): IRevenuePageProps => ({
    data: state.reports.revenue.chartDataSets,
    total: state.reports.revenue.total,
    isLoading: state.reports.revenue.isLoading,
    hasLoadingError: state.reports.revenue.hasLoadingError,
    isPhone: isPhoneScreen(state),
    contentWidth: state.ui.contentWidth
  })
)(RevenuePageFn)
